import { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import DateRangeCalendar from '../components/DateRangeCalendar';
import { dateKey, dayNumber, formatDate, isDateKey } from './bookingDates';
import { stayProperties } from './stayProperties';
import './BookingPage.css';
import StayRates from '../components/StayRates';
import BookingPrice from '../components/BookingPrice';
import GuestSelector from '../components/GuestSelector';

// Verified guest limits are applied where supplied. Without a confirmed limit,
// guest choices are enquiry party sizes. Live inventory is not supplied.
const destinations = Object.values(stayProperties);
const links = [{ label: 'Home', href: '/' }, { label: 'Stay', href: '/stay' }, { label: 'Explore', href: '/explore' }, { label: 'Our story', href: '/our-story' }, { label: 'Contact', href: '/contact' }];

export function validateBooking({ destination, range, guests }, today) {
  const errors = {};
  const property = destinations.find(item => item.id === destination);
  if (!property || property.comingSoon) errors.destination = 'This destination is coming soon and cannot be booked yet.';
  if (!range.start || !range.end) errors.dates = 'Choose both a check-in and a check-out date.';
  else if (!isDateKey(range.start) || !isDateKey(range.end)) errors.dates = 'Choose valid travel dates.';
  else if (range.start < today) errors.dates = 'Your check-in date cannot be in the past.';
  else if (dayNumber(range.end) - dayNumber(range.start) < 1) errors.dates = 'Choose a stay of at least one night.';
  if (!Number.isInteger(Number(guests)) || Number(guests) < 1 || Number(guests) > 10) errors.guests = 'Choose the number of guests travelling.';
  else if (property?.maxGuests && Number(guests) > property.maxGuests) errors.guests = `${property.name} accommodates up to ${property.maxGuests} guests. Choose a smaller guest count.`;
  return errors;
}

function initialBooking() {
  const query = new URLSearchParams(window.location.search);
  const today = dateKey(new Date());
  const start = query.get('checkIn') || '';
  const end = query.get('checkOut') || '';
  const validStart = isDateKey(start) && start >= today ? start : '';
  const requestedProperty = destinations.find(item => item.id === query.get('destination'));
  return {
    destination: destinations.some(item => item.id === query.get('destination') && !item.comingSoon) ? query.get('destination') : 'georgia',
    blockedDestination: Boolean(requestedProperty?.comingSoon),
    range: { start: validStart, end: validStart && isDateKey(end) && end > validStart ? end : '' },
    hasPet: query.get('pet') === '1',
    guests: /^(?:[1-9]|10)$/.test(query.get('guests') || '') ? query.get('guests') : '',
  };
}

export default function BookingPage() {
  const [initial] = useState(initialBooking);
  const [destination, setDestination] = useState(initial.destination);
  const [range, setRange] = useState(initial.range);
  const [guests, setGuests] = useState(initial.guests);
  const [hasPet, setHasPet] = useState(initial.hasPet);
  const [attempted, setAttempted] = useState(false);
  const [notice, setNotice] = useState(initial.blockedDestination ? 'Hillsboro, Oregon is coming soon and cannot be booked yet. Choose another destination or contact us for updates.' : '');
  const today = dateKey(new Date());
  const property = destinations.find(item => item.id === destination);
  const nights = range.start && range.end ? dayNumber(range.end) - dayNumber(range.start) : 0;
  // Recompute on every selection, including destination changes. No property
  // restrictions are assumed when capacity and inventory have not been supplied.
  const validation = validateBooking({ destination, range, guests }, today);
  const errors = attempted ? validation : { ...(guests && validation.guests ? { guests: validation.guests } : {}) };

  function submit(event) {
    event.preventDefault();
    setAttempted(true);
    const invalid = validateBooking({ destination, range, guests }, dateKey(new Date()));
    if (Object.keys(invalid).length) {
      setNotice('');
      const selector = invalid.destination ? '#booking-destination' : invalid.dates ? '[data-date][tabindex="0"]' : '#booking-guests';
      event.currentTarget.querySelector(selector)?.focus();
      return;
    }
    setNotice('Live availability is not connected yet. Your dates and guest count have been selected, but no availability, price or reservation has been confirmed. Please contact your host to discuss this stay.');
  }

  return (
    <>
      <Header homeHref="/" links={links} />
      <main id="main-content" className="escape-booking" tabIndex={-1}>
        <div className="escape-booking__inner">
          <div className="escape-booking__intro">
            <p className="escape-booking__eyebrow">PLAN YOUR ESCAPE</p>
            <h1>Good days begin here.</h1>
            <p>Choose your destination and dates to explore your stay.</p>
          </div>
          <ol className="escape-booking__progress" aria-label="Booking progress">
            <li aria-current="step"><span>01</span> Your stay</li><li><span>02</span> Guest details</li><li><span>03</span> Review &amp; payment</li>
          </ol>
          <div className="escape-booking__layout">
            <form className="escape-booking__card" onSubmit={submit} noValidate aria-label="Plan your escape">
              <fieldset id="booking-destination" tabIndex={-1} aria-describedby={errors.destination ? 'booking-destination-error' : undefined}>
                <legend>Where would you like to stay?</legend>
                <div className="escape-booking__destinations">
                  {destinations.map(item => <label key={item.id} className={destination === item.id ? 'is-selected' : ''}>
                    <input type="radio" name="destination" value={item.id} checked={destination === item.id} disabled={item.comingSoon} onChange={() => { setDestination(item.id); setNotice(''); }} />
                    <span>{item.name}{item.comingSoon ? ' — Coming soon' : ''}</span><span aria-hidden="true">{destination === item.id ? '✓' : '○'}</span>
                  </label>)}
                </div>
                {errors.destination && <p id="booking-destination-error" className="escape-booking__error" role="alert">{errors.destination}</p>}
              </fieldset>
              <h2>Choose your dates</h2>
              <DateRangeCalendar today={today} range={range} onChange={next => { setRange(next); setNotice(''); }} error={errors.dates} />
              {errors.dates && <p id="booking-dates-error" className="escape-booking__error" role="alert">{errors.dates}</p>}
              <dl className="escape-booking__selected-dates" aria-live="polite">
                <div><dt>Check-in</dt><dd>{formatDate(range.start)}</dd></div>
                <div><dt>Check-out</dt><dd>{formatDate(range.end)}</dd></div>
                <div><dt>Your stay</dt><dd>{nights ? `${nights} ${nights === 1 ? 'night' : 'nights'}` : '—'}</dd></div>
              </dl>
              <p className="escape-booking__guest-label" id="booking-guests-label">Guests</p>
              <GuestSelector id="booking-guests" value={Number(guests)} max={property.maxGuests || 10} error={Boolean(errors.guests)} describedBy={`booking-capacity${errors.guests ? ' booking-guests-error' : ''}`} onChange={next => { setGuests(next ? String(next) : ''); setNotice(''); }} />
              <p id="booking-capacity" className="escape-booking__fine-print">{property.maxGuests ? `Up to ${property.maxGuests} guests for this property.` : 'Guest capacity is not yet confirmed. Ask your host whether the home suits your group.'}</p>
              {errors.guests && <p id="booking-guests-error" className="escape-booking__error" role="alert">{errors.guests}</p>}
              <button className="escape-booking__submit" type="submit">Check availability <span aria-hidden="true">↗</span></button>
              <p className="escape-booking__fine-print">Live availability is not connected. No booking or payment will be made.</p>
              {notice && <p className="escape-booking__notice" role="status">{notice}</p>}
            </form>
            <aside className="escape-booking__sidebar" aria-label="Your stay summary">
              <div className="escape-booking__summary">
                {property.image ? <img src={`${process.env.PUBLIC_URL}/images/${property.image}`} alt={property.alt} width="900" height="600" /> : <div className="property-photo-pending">Property photographs to follow</div>}
                <div className="escape-booking__summary-copy">
                  <p className="escape-booking__eyebrow">YOUR NEXT ESCAPE</p>
                  <h2>{property.name}</h2>
                  <p className="escape-booking__fine-print">Destination: {property.destination}</p>
                  <StayRates property={property} />
                  <dl className="escape-booking__summary-details" aria-live="polite">
                    <div><dt>Check-in</dt><dd>{formatDate(range.start)}</dd></div>
                    <div><dt>Check-out</dt><dd>{formatDate(range.end)}</dd></div>
                    <div><dt>Guests</dt><dd>{guests ? `${guests} ${guests === '1' ? 'guest' : 'guests'}` : 'Select guests'}</dd></div>
                    <div><dt>Length of stay</dt><dd>{nights ? `${nights} ${nights === 1 ? 'night' : 'nights'}` : 'Select dates'}</dd></div>
                  </dl>
                  <p className="escape-booking__availability">{nights ? 'Contact your host to confirm availability, taxes and any additional fees.' : 'Choose dates to plan your stay. Your host can confirm availability and booking conditions.'}</p>
                  <BookingPrice property={property} range={range} hasPet={hasPet} onPetChange={value => { setHasPet(value); setNotice(''); }} />
                </div>
              </div>
              <div className="escape-booking__help"><h2>Need a hand planning?</h2><p>Ask your host about the stay.</p><a href="mailto:support@jamrockescape.com">support@jamrockescape.com</a><a href="/contact">Contact your host <span aria-hidden="true">↗</span></a></div>
              {property.airbnb && <div className="escape-booking__help"><h2>Book through Airbnb</h2><p>View live rates, availability and booking conditions on Airbnb.</p><a href={property.airbnb} target="_blank" rel="noopener noreferrer">View on Airbnb (new tab) <span aria-hidden="true">↗</span></a></div>}
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
